import * as React from 'react';
import { useState } from 'react';

import classNames from 'classnames';

import user from 'js/lib/user';

import { Button } from '@coursera/cds-core';

import A11yScreenReaderOnly from 'bundles/a11y/components/A11yScreenReaderOnly';
import { isS12nEligibleForCourseraPlusUpgrade } from 'bundles/coursera-plus/utils/courseraPlusUpgradeUtils';
import EnrollmentChoiceTypes from 'bundles/enroll-course/common/EnrollmentChoiceTypes';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-course/lib/enrollmentChoiceUtils';
import type { ApiError } from 'bundles/enroll/utils/errorUtils';
import { useEnrollModalEventing } from 'bundles/enroll/utils/eventingUtils';
import adsTracker from 'bundles/event-tracking/lib';
import Icon from 'bundles/iconfont/Icon';
import TrackedButton from 'bundles/page/components/TrackedButton';
import { getTrackingData } from 'bundles/promotions/utils/productDiscountPromoUtils';

import _t from 'i18n!nls/enroll';

import 'css!./__styles__/SubscriptionEnrollButton';

type Props = {
  s12nMonthlySkuId: string;
  s12nId?: string;
  isFreeTrial?: boolean;
  courseId?: string;
  promoCode?: string;
  onError: (error: ApiError) => void;
};

const SubscriptionEnrollButton: React.FunctionComponent<Props> = ({
  s12nMonthlySkuId,
  s12nId,
  isFreeTrial,
  courseId,
  promoCode,
  onError,
}) => {
  const [enrolling, setEnrolling] = useState(false);
  const { trackEnrollModalContinue } = useEnrollModalEventing();

  const handleError = (error: ApiError) => {
    setEnrolling(false);
    onError(error);
  };

  const onClick = async () => {
    const enrollmentChoiceType = EnrollmentChoiceTypes.ENROLL_THROUGH_S12N_SUBSCRIPTION;

    // Track S12nSubscriptionEnrollButtonClick GTM event
    adsTracker.trackS12nEnrollModalButtonClick({ userId: user.get()?.id, productId: s12nId });

    trackEnrollModalContinue(enrollmentChoiceType);

    setEnrolling(true);

    // Skip the s12n welcome email if user can enroll in free trial, user is in Plus upsell epic,
    // and the s12n is eligible for the Plus upgrade
    let skipWelcomeEmail = false;
    if (s12nId && isFreeTrial) {
      skipWelcomeEmail = await isS12nEligibleForCourseraPlusUpgrade(s12nId);
    }

    const handleSubmitPromise = choiceTypeToHandleSubmitPromise[enrollmentChoiceType];
    const options = { productSkuId: s12nMonthlySkuId, courseId, skipWelcomeEmail };

    return submitEnrollmentPromise({
      handleSubmitPromise,
      options,
      promoCode,
    }).catch(handleError);
  };

  const renderButtonContent = () => {
    if (enrolling) {
      // return
      return '';
    } else {
      return isFreeTrial ? _t('Start Free Trial') : _t('Subscribe');
    }
  };

  const trackingName = isFreeTrial ? 'free_trial_start_button' : 'enroll_subscribe_monthly';

  const buttonClassName = classNames('subscribe-button', {
    'primary cozy': !enrolling,
  });

  const buttonIcon = enrolling ? <Icon name="spinner" spin /> : undefined;

  return (
    <div className="rc-SubscriptionEnrollButton" role="status" aria-live="polite">
      <Button
        component={TrackedButton}
        data-e2e="subscription_enroll_button"
        trackingName={trackingName}
        data={getTrackingData()}
        className={buttonClassName}
        onClick={onClick}
        disabled={enrolling}
        withVisibilityTracking={false}
        requireFullyVisible={false}
        icon={buttonIcon}
        aria-live="polite"
      >
        {renderButtonContent()}
        {enrolling && <A11yScreenReaderOnly tagName="span">{_t('Loading')}</A11yScreenReaderOnly>}
      </Button>
    </div>
  );
};

export default SubscriptionEnrollButton;
